"use client";

// Client component for one reason: the photo's slow drift is a time-based animation, so it has to stop when the block
// is off screen or the tab is hidden. useLife() does that and nothing else — everything the reader needs (the heading,
// the three cards, the photo) is in the server-rendered HTML and reads without JavaScript.

import { useEffect, useRef } from "react";
import Image from "next/image";
import SectionHeading from "@/components/ds/section-heading";
import Reveal from "@/components/motion/reveal";
import { stagger } from "@/components/motion/stagger";
import { brandText, type Brand } from "./brand";
import { SECTION_IDS } from "./sections";
import "@/components/ds/button.css"; // .ds-edge: the gradient stroke of the glass cards
import "./deploy.css";

/** The three cards of the production block (www.north.law, 17/09), word for word. */
export const DEPLOY_CARDS = [
  { key: "On-site", title: "We come to you", body: "Our team embeds with your firm and maps where the hours go, in person." },
  { key: "Customized", title: "Tuned to your firm", body: "Your brain, your skills, your house style. The secret sauce, not a generic setup." },
  { key: "Firm-wide", title: "Every desk", body: "Claude goes live for partners, associates, paralegals, and clerks alike." },
] as const;

/* Sets data-motion on the card: "live" (the photo drifts), "idle" (the drift is paused where it is — off screen or on a
   hidden tab), absent under reduced motion (deploy.css only drifts under data-motion, so nothing moves at all). The
   scroll-driven zoom and parallax need none of this: they advance with the scroll and stand still otherwise. */
function useLife() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const card = ref.current;
    if (!card) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    let visible = false;

    const update = () => {
      if (reduce.matches) delete card.dataset.motion;
      else card.dataset.motion = visible && !document.hidden ? "live" : "idle";
    };
    const io = new IntersectionObserver(
      ([entry]) => {
        visible = !!entry?.isIntersecting;
        update();
      },
      { threshold: 0 },
    );
    io.observe(card);
    document.addEventListener("visibilitychange", update);
    reduce.addEventListener("change", update);
    return () => {
      io.disconnect();
      document.removeEventListener("visibilitychange", update);
      reduce.removeEventListener("change", update);
    };
  }, []);

  return ref;
}

/* `brand` (brand.ts): "north" unless the /logo-test preview says "habit". */
export default function Deploy({ brand = "north" }: { brand?: Brand }) {
  const card = useLife();

  return (
    <Reveal as="section" className="v2-deploy" id={SECTION_IDS.deploy}>
      {/* A card, not a full-bleed band: the same 8 px of white around it and the same radius 8 as the mission card
          further down (components/home-v2/manifesto.css) and as the photo card at the top of the page. The photo and
          its veil live INSIDE it and are cut by its radius. */}
      <div ref={card} className="v2-deploy__card">
        {/* Three nested layers, one property each, so three movements never fight over one transform: the frame takes
            the scroll (zoom on scale, parallax on translate), the sail the slow drift across, the photo the slow drift
            down. The frame bleeds past the card on every side, so no edge ever shows. */}
        <div className="v2-deploy__frame">
          <div className="v2-deploy__sail">
            <Image
              src="/assets/v2/product/deploy-bkg.jpg"
              alt={brandText("The North team around a meeting table with lawyers, the Manhattan skyline behind them", brand)}
              fill
              sizes="100vw"
              className="v2-deploy__photo"
            />
          </div>
        </div>
        <div className="v2-deploy__inner">
          {/* reveal: tag, title and lead rise one after the other (--i 0 to 2), then the cards (deploy.css). */}
          <SectionHeading
            reveal
            tone="dark"
            className="v2-deploy__text"
            tag={brandText("North Deploy", brand)}
            tagVariant="glass"
            title={["A team of Forward Deployed Legal Engineers,", "in your office."]}
            lead="We come to you, learn how your firm works, and tailor Claude to your practice. Then we stay with you as your external Chief AI Officer. Always here, always on: bringing you updates, new features, and custom tools and skills as your firm evolves."
          />

          {/* A loose stack of glass cards right of the copy (production's layout): each step places and tilts its card
              and drifts with the scroll; the card itself is revealed and lifts on hover. Stacked under the copy below
              900 px. */}
          <ul className="v2-deploy__cards">
            {DEPLOY_CARDS.map((step, i) => (
              <li key={step.key} className="v2-deploy__step">
                <article className="v2-deploy__glass ds-edge v2-reveal" style={stagger(4 + i * 2)}>
                  <p className="v2-deploy__key">{step.key}</p>
                  <h3 className="v2-deploy__title">{step.title}</h3>
                  <p className="v2-deploy__body">{step.body}</p>
                </article>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Reveal>
  );
}
